import { Tag, TextCaption, Tooltip } from '@pypestream/design-system';

import type { OrgSecret } from './maestro-org-api';
import type { OrgSecretRow } from './org-secret-rows';
import { scopeLabel, useSecretsText } from './org-secrets-text';

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

export function NameCell({ row }: { row: OrgSecretRow }) {
  const text = useSecretsText();
  const violations = row.secret?.violations ?? [];
  return (
    <>
      <code>{row.name}</code>
      {violations.length > 0 && (
        <>
          {' '}
          <Tooltip
            content={[
              ...violations.map((v) => text(`violations.${v}`)),
              text('violations.help'),
            ].join(' ')}
          >
            <Tag size="small" variant="warning" test-id="org-secret-violation">
              {text('violations.tag')}
            </Tag>
          </Tooltip>
        </>
      )}
      {row.secret?.description && (
        <TextCaption className="c-org-sub">
          {row.secret.description}
        </TextCaption>
      )}
    </>
  );
}

export function ValueCell({ row }: { row: OrgSecretRow }) {
  const text = useSecretsText();
  const value = row.secret?.value;
  if (row.kind === 'variable' && value != null) {
    return (
      <code className="c-org-value" title={value}>
        {value}
      </code>
    );
  }
  const label =
    row.kind === 'variable'
      ? 'value.variable'
      : row.secret && !row.secret.set
        ? 'value.notSet'
        : 'value.secret';
  return <Tag size="small">{text(label)}</Tag>;
}

export function ScopeCell({ row }: { row: OrgSecretRow }) {
  const text = useSecretsText();
  const { scope } = row;
  const inProject =
    (scope.type === 'application' || scope.type === 'agent') && scope.project;
  return (
    <>
      {scopeLabel(text, scope)}
      {inProject && (
        <TextCaption className="c-org-sub" variant="secondary">
          {text('scope.inProject', { project: inProject.name })}
        </TextCaption>
      )}
    </>
  );
}

/** Spec 6.2: date, who, and where the change was made; staff in a customer org read as Pypestream. */
export function updatedBy(
  text: (name: string, options?: Record<string, unknown>) => string,
  secret: Pick<OrgSecret, 'updatedBy' | 'updatedVia'>
): string | null {
  const { updatedBy: author, updatedVia: via } = secret;
  const who = author
    ? author.pypestream
      ? text('updated.pypestream')
      : (author.name ?? text('updated.unknown'))
    : null;
  const where =
    via && ['manager', 'studio', 'builder', 'liveview'].includes(via)
      ? text(`updated.via.${via}`)
      : null;
  const parts = [who && text('updated.by', { user: who }), where].filter(
    Boolean
  );
  return parts.length > 0 ? parts.join(' ') : null;
}

export function UpdatedCell({ row }: { row: OrgSecretRow }) {
  const text = useSecretsText();
  const date = formatDate(row.secret?.updatedAt ?? null);
  if (!row.secret || !date) return <span className="c-org-muted">—</span>;
  const by = updatedBy(text, row.secret);
  return (
    <>
      {date}
      {by && (
        <TextCaption className="c-org-sub" variant="secondary">
          {by}
        </TextCaption>
      )}
    </>
  );
}
